/**
 * Valid route prefixes — gates app-shell widgets that should only mount on
 * known TanStack Router paths.
 * Routes NOT in this list fall through to `composeInvalidRouteProviders`,
 * which omits `SessionProvider` and friends so a real 404 doesn't hit the
 * auth API. Forgetting to register a new top-level route here makes every
 * page on it crash with `useSessionValue must be used within SessionProvider`.
 *
 * Source of truth: the code-based route config in `web/src/app/router/router.ts`.
 * The accompanying test extracts its `path` entries and fails with a precise
 * diff when a new top-level path is added without a matching entry here.
 */
export const VALID_ROUTE_PREFIXES = [
	"/",
	"/account",
	"/accept-invitation",
	"/alternate",
	"/alternate2",
	"/alternate3",
	"/alternate4",
	"/activity",
	"/superuser",
	"/billing",
	"/calendar",
	"/campaigns",
	"/chat",
	"/config",
	"/contacts",
	"/dashboard",
	"/debug",
	"/demo",
	"/docs",
	"/documents",
	"/home",
	"/legal",
	"/login",
	"/members",
	"/onboarding",
	"/org",
	"/pending-org",
	"/playground",
	"/roles",
	"/solutions",
	"/waitlist",
] as const;

/**
 * Checks if a pathname is a valid route that should render the sidebar and chat components.
 * @param pathname - The current pathname
 * @returns true if the pathname is valid, false otherwise
 */
export function isValidRoute(pathname: string | null): boolean {
	if (!pathname) {
		return false;
	}

	// Exact matches for root
	if (pathname === "/") {
		return true;
	}

	// Check if pathname matches any valid route prefix exactly or is a valid sub-path
	return VALID_ROUTE_PREFIXES.some((prefix) => {
		if (prefix === "/") {
			return false; // Already handled above
		}
		// Exact match
		if (pathname === prefix) {
			return true;
		}
		// Valid sub-path: must start with prefix followed by '/' (e.g., /documents/123, /dashboard/analytics)
		// This prevents false matches like /documentse42 matching /documents
		return pathname.startsWith(`${prefix}/`);
	});
}
